import { useState } from 'react';
import Box from '@mui/material/Box';
import Dialog from '@mui/material/Dialog';
import Grid from '@mui/material/Grid';
import { useAccountMetricsQuery } from '../../../services/api';
import { api as apiClient } from '../../../services/apiClient';
import { CHMAP, CHCOLOR } from '../../../constants/channels';
import { inr, inrShort } from '../../../utils/format';
import AppButton from '../../../components/common/AppButton';
import DataCard from '../../../components/common/DataCard';
import TableCard from '../../../components/common/TableCard';
import QueryState from '../../../components/common/QueryState';
import { Shimmer, ChartSkeleton, TableSkeleton } from '../../../components/common/Shimmer';
import { useToast } from '../../../components/common/Toast';
import MonthlyBars from '../../../components/charts/MonthlyBars';

function Stat({ label, value, extra, color }) {
  return (
    <Box sx={{ background: 'var(--card-2)', border: '1px solid var(--border)', borderRadius: '12px', p: '13px 14px', height: '100%' }}>
      <Box sx={{ fontFamily: 'var(--mono)', fontSize: 10, letterSpacing: '.06em', textTransform: 'uppercase', color: 'var(--muted)' }}>
        {label}
      </Box>
      <Box sx={{ fontFamily: 'var(--disp)', fontWeight: 700, fontSize: 22, mt: 0.75, color }}>
        {value}
        {extra && <Box component="span" sx={{ fontSize: 11, color: 'var(--muted)', fontWeight: 400 }}> · {extra}</Box>}
      </Box>
    </Box>
  );
}

/** Four stat tiles, the monthly chart, then the month-by-month table. */
function MetricsSkeleton() {
  return (
    <>
      <Grid container spacing={1.5}>
        {[0, 1, 2, 3].map((i) => (
          <Grid key={i} size={{ xs: 12, sm: 6, md: 3 }}><Shimmer height={78} radius={12} /></Grid>
        ))}
      </Grid>
      <Box sx={{ mt: 2 }}><ChartSkeleton height={200} bars={12} /></Box>
      <Box sx={{ mt: 2 }}><TableSkeleton columns={4} rows={6} title={false} /></Box>
    </>
  );
}

/** Last-12-months business metrics for one account, aggregated from the ledger. */
export default function MetricsModal({ open, channelId, account, onClose }) {
  const toast = useToast();
  const [exporting, setExporting] = useState(false);

  // `skip` keeps the closed modal from fetching for whichever row was last opened.
  const query = useAccountMetricsQuery(account?.id, { skip: !open || !account?.id });

  const exportCSV = async () => {
    setExporting(true);
    try {
      // the CSV comes from the same aggregate the chart draws, so the two can't drift
      await apiClient.download(`/accounts/${account.id}/metrics/export`, {}, 'metrics.csv');
    } catch (e) {
      toast(e?.message || "Couldn't export those metrics", 'error');
    } finally {
      setExporting(false);
    }
  };

  if (!account) return null;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      slotProps={{ paper: { sx: { borderRadius: '16px', border: '1px solid var(--border)', background: 'var(--card)' } } }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', p: '18px 22px', borderBottom: '1px solid var(--border)' }}>
        <Box>
          <Box sx={{ fontFamily: 'var(--disp)', fontSize: 18, fontWeight: 700 }}>{account.name}</Box>
          <Box sx={{ fontSize: 12, color: 'var(--muted)', fontFamily: 'var(--mono)', textTransform: 'uppercase', letterSpacing: '.05em', mt: '2px' }}>
            {CHMAP[channelId].name} · Business metrics
          </Box>
        </Box>
        <AppButton mini onClick={onClose} sx={{ width: 34, height: 34 }}>✕</AppButton>
      </Box>

      <Box sx={{ p: '20px 22px', maxHeight: '70vh', overflowY: 'auto' }}>
        <QueryState query={query} skeleton={<MetricsSkeleton />}>
          {(m) => (
            <>
              <Grid container spacing={1.5}>
                <Grid size={{ xs: 12, sm: 6, md: 3 }}><Stat label="Last 12 mo. sales" value={inrShort(m.total)} /></Grid>
                <Grid size={{ xs: 12, sm: 6, md: 3 }}><Stat label="Avg / month" value={inrShort(m.avg)} /></Grid>
                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                  <Stat label="Best month" value={inrShort(m.best?.v || 0)} extra={m.best?.label} />
                </Grid>
                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                  <Stat
                    label="YoY growth"
                    value={`${m.growth >= 0 ? '▲' : '▼'} ${Math.abs(m.growth).toFixed(1)}%`}
                    color={m.growth >= 0 ? 'var(--good-ink)' : 'var(--crit)'}
                  />
                </Grid>
              </Grid>

              <DataCard
                title="Monthly sales — last 12 months"
                subtitle={m.empty ? 'Nothing billed against this account yet' : 'Hover a bar for the exact figure'}
                sx={{ mt: 2, boxShadow: 'none' }}
              >
                {m.empty ? (
                  <Box sx={{ color: 'var(--muted)', fontSize: 13, p: '28px 0', textAlign: 'center' }}>
                    No ledger transactions are billed to this account in the last 12 months.
                  </Box>
                ) : (
                  <MonthlyBars data={m.series} color={CHCOLOR[channelId]} />
                )}
              </DataCard>

              <TableCard sx={{ mt: 2 }}>
                <table className="tbl">
                  <thead>
                    <tr>
                      <th>Month</th>
                      <th className="num">Sales</th>
                      <th className="num">Transactions</th>
                      <th className="num">MoM change</th>
                    </tr>
                  </thead>
                  <tbody>
                    {m.series.map((d, i) => {
                      const prev = i ? m.series[i - 1].v : null;
                      const mom = prev ? (100 * (d.v - prev)) / prev : null;
                      return (
                        <tr key={d.month}>
                          <td>{d.label}</td>
                          <td className="num">{inr(d.v)}</td>
                          <td className="num">{d.n}</td>
                          <td className="num">
                            {mom == null ? '—' : (
                              <span style={{ color: mom >= 0 ? 'var(--good-ink)' : 'var(--crit)' }}>
                                {mom >= 0 ? '▲' : '▼'} {Math.abs(mom).toFixed(1)}%
                              </span>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                    <tr style={{ fontWeight: 700, background: 'var(--card-2)' }}>
                      <td>Total</td>
                      <td className="num">{inr(m.total)}</td>
                      <td className="num">{m.series.reduce((t, d) => t + d.n, 0)}</td>
                      <td className="num">—</td>
                    </tr>
                  </tbody>
                </table>
              </TableCard>
            </>
          )}
        </QueryState>
      </Box>

      <Box sx={{ display: 'flex', gap: 1.25, justifyContent: 'flex-end', p: '16px 22px', borderTop: '1px solid var(--border)' }}>
        <AppButton onClick={exportCSV} disabled={exporting || !query.data}>
          {exporting ? 'Exporting…' : '⤓ Export CSV'}
        </AppButton>
        <AppButton tone="primary" onClick={onClose}>Close</AppButton>
      </Box>
    </Dialog>
  );
}
